import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../hooks';
import {
	useCategories,
	useCreateCategoryMutation,
	useCreateScenarioMutation,
	useCreateSkillAreaMutation,
	useDeleteCategoryMutation,
	useDeleteScenarioMutation,
	useDeleteSkillAreaMutation,
	useScenarios,
	useSkillAreas,
	useUpdateCategoryMutation,
	useUpdateScenarioMutation,
	useUpdateSkillAreaMutation,
} from '../../../queries/Scenarios';
import type {
	Category,
	CreateCategoryInput,
	CreateScenarioInput,
	CreateSkillAreaInput,
	Scenario,
	SkillArea,
	UpdateCategoryInput,
	UpdateScenarioInput,
	UpdateSkillAreaInput,
} from '../../../voice-agent/types/scenarios';
import { Button } from '../../../../web/components-v2/Button';
import {
	Dialog,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { BasicPage } from '../../BasicPage';
import { CategoryForm } from './CategoryForm';
import { ScenarioForm } from './ScenarioForm';
import { SkillAreaForm } from './SkillAreaForm';
import { CategoryRow } from './CategoryRow';
import { styleSheet } from './styles';

/**
 * Grouped data structure for hierarchical display
 * Category -> SkillArea[] -> Scenario[]
 */
interface GroupedCategory {
	category: Category;
	skillAreas: GroupedSkillArea[];
}

interface GroupedSkillArea {
	skillArea: SkillArea;
	scenarios: Scenario[];
}

interface IProps {
	className?: string;
}

/**
 * ScenarioManagement page component
 *
 * Provides a unified interface for managing the scenario hierarchy:
 * Categories -> Skill Areas -> Scenarios
 *
 * All items are displayed in a nested, collapsible view with inline actions.
 */
export const ScenarioManagement: React.FC<IProps> = ({ className = '' }) => {
	const errorMessages = useErrorMessages();

	// Fetch all data
	const { data: categoriesData, isPending: categoriesLoading, error: categoriesError } = useCategories();
	const { data: skillAreasData, isPending: skillAreasLoading, error: skillAreasError } = useSkillAreas();
	const { data: scenariosData, isPending: scenariosLoading, error: scenariosError } = useScenarios();

	// Mutations for Category
	const createCategoryMutation = useCreateCategoryMutation();
	const updateCategoryMutation = useUpdateCategoryMutation();
	const deleteCategoryMutation = useDeleteCategoryMutation();

	// Mutations for SkillArea
	const createSkillAreaMutation = useCreateSkillAreaMutation();
	const updateSkillAreaMutation = useUpdateSkillAreaMutation();
	const deleteSkillAreaMutation = useDeleteSkillAreaMutation();

	// Mutations for Scenario
	const createScenarioMutation = useCreateScenarioMutation();
	const updateScenarioMutation = useUpdateScenarioMutation();
	const deleteScenarioMutation = useDeleteScenarioMutation();

	// Delete loading states
	const [isDeletingCategory, setIsDeletingCategory] = React.useState(false);
	const [isDeletingSkillArea, setIsDeletingSkillArea] = React.useState(false);
	const [isDeletingScenario, setIsDeletingScenario] = React.useState(false);

	// Expansion state - all collapsed by default
	const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set());
	const [expandedSkillAreas, setExpandedSkillAreas] = React.useState<Set<string>>(new Set());

	// Category dialog state
	const [categoryDialogOpen, setCategoryDialogOpen] = React.useState(false);
	const [editingCategory, setEditingCategory] = React.useState<Category | null>(null);
	const [deletingCategory, setDeletingCategory] = React.useState<Category | null>(null);

	// SkillArea dialog state
	const [skillAreaDialogOpen, setSkillAreaDialogOpen] = React.useState(false);
	const [editingSkillArea, setEditingSkillArea] = React.useState<SkillArea | null>(null);
	const [deletingSkillArea, setDeletingSkillArea] = React.useState<SkillArea | null>(null);
	const [skillAreaParentId, setSkillAreaParentId] = React.useState<string | null>(null);

	// Scenario dialog state
	const [scenarioDialogOpen, setScenarioDialogOpen] = React.useState(false);
	const [editingScenario, setEditingScenario] = React.useState<Scenario | null>(null);
	const [deletingScenario, setDeletingScenario] = React.useState<Scenario | null>(null);
	const [scenarioParentId, setScenarioParentId] = React.useState<string | null>(null);

	// Memoize arrays from data
	const categories = React.useMemo(() => categoriesData || [], [categoriesData]);
	const skillAreas = React.useMemo(() => skillAreasData || [], [skillAreasData]);
	const scenarios = React.useMemo(() => scenariosData || [], [scenariosData]);

	// Group data hierarchically: Category -> SkillArea[] -> Scenario[]
	const groupedData = React.useMemo((): GroupedCategory[] => {
		// Create a map of skillAreaId -> scenarios
		const scenariosBySkillArea = new Map<string, Scenario[]>();
		for (const scenario of scenarios) {
			const existing = scenariosBySkillArea.get(scenario.skillAreaId) || [];
			existing.push(scenario);
			scenariosBySkillArea.set(scenario.skillAreaId, existing);
		}

		// Create a map of categoryId -> skillAreas with their scenarios
		const skillAreasByCategory = new Map<string, GroupedSkillArea[]>();
		for (const skillArea of skillAreas) {
			const existing = skillAreasByCategory.get(skillArea.categoryId) || [];
			existing.push({
				skillArea,
				scenarios: scenariosBySkillArea.get(skillArea.id) || [],
			});
			skillAreasByCategory.set(skillArea.categoryId, existing);
		}

		// Build the final grouped structure
		return categories.map(category => ({
			category,
			skillAreas: skillAreasByCategory.get(category.id) || [],
		}));
	}, [categories, skillAreas, scenarios]);

	// Toggle category expansion
	const toggleCategory = React.useCallback((categoryId: string) => {
		setExpandedCategories(prev => {
			const next = new Set(prev);
			if (next.has(categoryId)) {
				next.delete(categoryId);
			} else {
				next.add(categoryId);
			}
			return next;
		});
	}, []);

	// Toggle skill area expansion
	const toggleSkillArea = React.useCallback((skillAreaId: string) => {
		setExpandedSkillAreas(prev => {
			const next = new Set(prev);
			if (next.has(skillAreaId)) {
				next.delete(skillAreaId);
			} else {
				next.add(skillAreaId);
			}
			return next;
		});
	}, []);

	// Category handlers
	const handleAddCategory = React.useCallback(() => {
		setEditingCategory(null);
		setCategoryDialogOpen(true);
	}, []);

	const handleEditCategory = React.useCallback((category: Category) => {
		setEditingCategory(category);
		setCategoryDialogOpen(true);
	}, []);

	const handleDeleteCategory = React.useCallback((category: Category) => {
		setDeletingCategory(category);
	}, []);

	// SkillArea handlers
	const handleAddSkillArea = React.useCallback((categoryId: string) => {
		setEditingSkillArea(null);
		setSkillAreaParentId(categoryId);
		setSkillAreaDialogOpen(true);
	}, []);

	const handleEditSkillArea = React.useCallback((skillArea: SkillArea) => {
		setEditingSkillArea(skillArea);
		setSkillAreaParentId(skillArea.categoryId);
		setSkillAreaDialogOpen(true);
	}, []);

	const handleDeleteSkillArea = React.useCallback((skillArea: SkillArea) => {
		setDeletingSkillArea(skillArea);
	}, []);

	// Scenario handlers
	const handleAddScenario = React.useCallback((skillAreaId: string) => {
		setEditingScenario(null);
		setScenarioParentId(skillAreaId);
		setScenarioDialogOpen(true);
	}, []);

	const handleEditScenario = React.useCallback((scenario: Scenario) => {
		setEditingScenario(scenario);
		setScenarioParentId(scenario.skillAreaId);
		setScenarioDialogOpen(true);
	}, []);

	const handleDeleteScenario = React.useCallback((scenario: Scenario) => {
		setDeletingScenario(scenario);
	}, []);

	// Category form submit handler
	const handleCategoryFormSubmit = async (input: CreateCategoryInput | UpdateCategoryInput) => {
		try {
			if (editingCategory) {
				await updateCategoryMutation.mutateAsync({
					id: editingCategory.id,
					input: input as UpdateCategoryInput,
				});
				toast.success('Category updated successfully');
			} else {
				await createCategoryMutation.mutateAsync(input as CreateCategoryInput);
				toast.success('Category created successfully');
			}
			setCategoryDialogOpen(false);
			setEditingCategory(null);
		} catch (err) {
			errorMessages.pushApiError(err);
		}
	};

	// Category delete confirm handler
	const handleCategoryDeleteConfirm = async () => {
		if (!deletingCategory) return;
		setIsDeletingCategory(true);
		try {
			await deleteCategoryMutation.mutateAsync(deletingCategory.id);
			toast.success('Category deleted successfully');
			setDeletingCategory(null);
		} catch (err) {
			errorMessages.pushApiError(err);
		} finally {
			setIsDeletingCategory(false);
		}
	};

	// SkillArea form submit handler
	const handleSkillAreaFormSubmit = async (input: CreateSkillAreaInput | UpdateSkillAreaInput) => {
		try {
			if (editingSkillArea) {
				await updateSkillAreaMutation.mutateAsync({
					id: editingSkillArea.id,
					input: input as UpdateSkillAreaInput,
				});
				toast.success('Skill area updated successfully');
			} else {
				await createSkillAreaMutation.mutateAsync(input as CreateSkillAreaInput);
				toast.success('Skill area created successfully');
			}
			setSkillAreaDialogOpen(false);
			setEditingSkillArea(null);
			setSkillAreaParentId(null);
		} catch (err) {
			errorMessages.pushApiError(err);
		}
	};

	// SkillArea delete confirm handler
	const handleSkillAreaDeleteConfirm = async () => {
		if (!deletingSkillArea) return;
		setIsDeletingSkillArea(true);
		try {
			await deleteSkillAreaMutation.mutateAsync(deletingSkillArea.id);
			toast.success('Skill area deleted successfully');
			setDeletingSkillArea(null);
		} catch (err) {
			errorMessages.pushApiError(err);
		} finally {
			setIsDeletingSkillArea(false);
		}
	};

	// Scenario form submit handler
	const handleScenarioFormSubmit = async (input: CreateScenarioInput | UpdateScenarioInput) => {
		try {
			if (editingScenario) {
				await updateScenarioMutation.mutateAsync({
					id: editingScenario.id,
					input: input as UpdateScenarioInput,
				});
				toast.success('Scenario updated successfully');
			} else {
				await createScenarioMutation.mutateAsync(input as CreateScenarioInput);
				toast.success('Scenario created successfully');
			}
			setScenarioDialogOpen(false);
			setEditingScenario(null);
			setScenarioParentId(null);
		} catch (err) {
			errorMessages.pushApiError(err);
		}
	};

	// Scenario delete confirm handler
	const handleScenarioDeleteConfirm = async () => {
		if (!deletingScenario) return;
		setIsDeletingScenario(true);
		try {
			await deleteScenarioMutation.mutateAsync(deletingScenario.id);
			toast.success('Scenario deleted successfully');
			setDeletingScenario(null);
		} catch (err) {
			errorMessages.pushApiError(err);
		} finally {
			setIsDeletingScenario(false);
		}
	};

	// Loading state
	const isLoading = categoriesLoading || skillAreasLoading || scenariosLoading;
	const hasError = categoriesError || skillAreasError || scenariosError;

	// Render content based on state
	const renderContent = () => {
		if (isLoading) {
			return (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner />
				</div>
			);
		}

		if (hasError) {
			return (
				<div className={css(styleSheet.errorState)}>
					<p>Error loading data. Please try again.</p>
				</div>
			);
		}

		if (groupedData.length === 0) {
			return (
				<div className={css(styleSheet.emptyState)}>
					<p>No categories found. Create your first category to get started.</p>
					<Button variant='primary' onClick={handleAddCategory}>
						+ Add Category
					</Button>
				</div>
			);
		}

		return (
			<div className={css(styleSheet.rowsContainer)}>
				{groupedData.map(({ category, skillAreas: groupedSkillAreas }) => (
					<CategoryRow
						key={category.id}
						category={category}
						skillAreas={groupedSkillAreas}
						isExpanded={expandedCategories.has(category.id)}
						onToggle={() => toggleCategory(category.id)}
						onEdit={() => handleEditCategory(category)}
						onDelete={() => handleDeleteCategory(category)}
						onAddSkillArea={() => handleAddSkillArea(category.id)}
						onEditSkillArea={handleEditSkillArea}
						onDeleteSkillArea={handleDeleteSkillArea}
						onAddScenario={handleAddScenario}
						onEditScenario={handleEditScenario}
						onDeleteScenario={handleDeleteScenario}
						expandedSkillAreas={expandedSkillAreas}
						onToggleSkillArea={toggleSkillArea}
					/>
				))}
			</div>
		);
	};

	return (
		<BasicPage
			appearance='light'
			className={className}
			title='Scenario Management'
			headerActions={
				<Button variant='primary' onClick={handleAddCategory}>
					+ Add Category
				</Button>
			}
		>
			{renderContent()}

			{/* Category Create/Edit Dialog */}
			<Dialog
				open={categoryDialogOpen}
				onOpenChange={open => {
					if (!open) {
						setCategoryDialogOpen(false);
						setEditingCategory(null);
					}
				}}
			>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>{editingCategory ? 'Edit Category' : 'Create New Category'}</DialogTitle>
					</DialogHeader>
					<CategoryForm
						category={editingCategory}
						onSubmit={handleCategoryFormSubmit}
						onCancel={() => {
							setCategoryDialogOpen(false);
							setEditingCategory(null);
						}}
						isSubmitting={createCategoryMutation.isPending || updateCategoryMutation.isPending}
					/>
				</DialogPopup>
			</Dialog>

			{/* Category Delete Confirmation Dialog */}
			<Dialog
				open={!!deletingCategory}
				onOpenChange={open => {
					if (!open) {
						setDeletingCategory(null);
					}
				}}
			>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Delete Category?</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete &quot;{deletingCategory?.name}&quot;? This will also delete all associated
							skill areas and scenarios. This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button onClick={() => setDeletingCategory(null)} variant='secondary' disabled={isDeletingCategory}>
							Cancel
						</Button>
						<Button onClick={handleCategoryDeleteConfirm} variant='destructive-secondary' disabled={isDeletingCategory}>
							{isDeletingCategory ? 'Deleting...' : 'Delete'}
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>

			{/* SkillArea Create/Edit Dialog */}
			<Dialog
				open={skillAreaDialogOpen}
				onOpenChange={open => {
					if (!open) {
						setSkillAreaDialogOpen(false);
						setEditingSkillArea(null);
						setSkillAreaParentId(null);
					}
				}}
			>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>{editingSkillArea ? 'Edit Skill Area' : 'Create New Skill Area'}</DialogTitle>
					</DialogHeader>
					<SkillAreaForm
						skillArea={editingSkillArea}
						defaultCategoryId={skillAreaParentId}
						onSubmit={handleSkillAreaFormSubmit}
						onCancel={() => {
							setSkillAreaDialogOpen(false);
							setEditingSkillArea(null);
							setSkillAreaParentId(null);
						}}
						isSubmitting={createSkillAreaMutation.isPending || updateSkillAreaMutation.isPending}
					/>
				</DialogPopup>
			</Dialog>

			{/* SkillArea Delete Confirmation Dialog */}
			<Dialog
				open={!!deletingSkillArea}
				onOpenChange={open => {
					if (!open) {
						setDeletingSkillArea(null);
					}
				}}
			>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Delete Skill Area?</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete &quot;{deletingSkillArea?.name}&quot;? This will also delete all
							associated scenarios. This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button onClick={() => setDeletingSkillArea(null)} variant='secondary' disabled={isDeletingSkillArea}>
							Cancel
						</Button>
						<Button
							onClick={handleSkillAreaDeleteConfirm}
							variant='destructive-secondary'
							disabled={isDeletingSkillArea}
						>
							{isDeletingSkillArea ? 'Deleting...' : 'Delete'}
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>

			{/* Scenario Create/Edit Dialog */}
			<Dialog
				open={scenarioDialogOpen}
				onOpenChange={open => {
					if (!open) {
						setScenarioDialogOpen(false);
						setEditingScenario(null);
						setScenarioParentId(null);
					}
				}}
			>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>{editingScenario ? 'Edit Scenario' : 'Create New Scenario'}</DialogTitle>
					</DialogHeader>
					<ScenarioForm
						scenario={editingScenario}
						defaultSkillAreaId={scenarioParentId}
						onSubmit={handleScenarioFormSubmit}
						onCancel={() => {
							setScenarioDialogOpen(false);
							setEditingScenario(null);
							setScenarioParentId(null);
						}}
						isSubmitting={createScenarioMutation.isPending || updateScenarioMutation.isPending}
					/>
				</DialogPopup>
			</Dialog>

			{/* Scenario Delete Confirmation Dialog */}
			<Dialog
				open={!!deletingScenario}
				onOpenChange={open => {
					if (!open) {
						setDeletingScenario(null);
					}
				}}
			>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Delete Scenario?</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete &quot;{deletingScenario?.name}&quot;? This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button onClick={() => setDeletingScenario(null)} variant='secondary' disabled={isDeletingScenario}>
							Cancel
						</Button>
						<Button onClick={handleScenarioDeleteConfirm} variant='destructive-secondary' disabled={isDeletingScenario}>
							{isDeletingScenario ? 'Deleting...' : 'Delete'}
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</BasicPage>
	);
};
